import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdInput, CmdLabel, CmdLoader } from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IAddWaypointRequestData,
  IDetailWaypointResponseData,
  IFormWaypointResponseData,
  IWaypointPostion,
} from './types';
import { convertCoordinatesToArray, getWaypointFormData, updateWaypoint } from './WaypointService';
import ConfirmDialog from '../../ConfirmDialog';
import SimpleSelectWithIcon from '../../Forms/ReactHookFormFields/SimpleSelectWithIcon';
import { WAYPOINT_QUERY_KEY } from './constants';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  activeFilterAtom,
  centerWaypointAtom,
  computedAreaAtom,
  coordinatesAtom,
  detailWaypointAtom,
  isCopyModeWpAtom,
  isEditModeAtom,
  showWaypointLoaderAtom,
  waypointDataAtom,
  waypointIdAtom,
} from './WaypointState';
import { STATUS_WARNING } from '../../../Services/HttpService';

interface IEditWaypointForm {
  closeEditWaypoint: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  saveEditWaypoint: () => void;
}

interface EditWaypointFormValues {
  icon: string;
  name: string;
  type: string;
  customer: string;
}

function EditWaypointForm(props: IEditWaypointForm): JSX.Element {
  const { formatMessage: f } = useIntl();
  const computedArea = useRecoilState<number>(computedAreaAtom)[0];
  const [detailWaypoint, setDetailWaypoint] = useRecoilState<IDetailWaypointResponseData | null>(
    detailWaypointAtom
  );
  const isCopyModeWp = useRecoilValue<boolean>(isCopyModeWpAtom);
  const [formOptions, setFormOptions] = React.useState<IFormWaypointResponseData | null>(null);
  const [customerLabel, setCustomerLabel] = React.useState<string>('');
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = React.useState(false);
  const coordinates = useRecoilValue<IWaypointPostion[] | null>(coordinatesAtom);
  const setIsEditMode = useSetRecoilState<boolean>(isEditModeAtom);
  const center = useRecoilValue<IWaypointPostion | null>(centerWaypointAtom);
  const [waypointData, setWaypointData] = useRecoilState<IDetailWaypointResponseData | null>(
    waypointDataAtom
  );
  const waypointId = useRecoilValue<number | null>(waypointIdAtom);
  const setShowWaypointLoader = useSetRecoilState(showWaypointLoaderAtom);
  const queryClient = useQueryClient();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const activeFilter = useRecoilValue(activeFilterAtom);
  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    setError,
    formState: { errors },
  } = useForm<EditWaypointFormValues>({
    mode: 'onChange',
    defaultValues: {
      icon:
        waypointData && waypointData.category && waypointData.category.id
          ? String(waypointData.category.id)
          : '',
      name: waypointData && waypointData.name ? waypointData.name : '',
      type: waypointData?.type ? waypointData.type : '',
      customer: waypointData && waypointData.customerId ? String(waypointData.customerId) : '',
    },
    resolver: async (data) => {
      const formValidation = {
        icon: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
        name: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      };

      const validateErros = validateFields(data, formValidation);

      let formErrors = transformErrors(validateErros);

      if (data.name && data.name.trim().length <= 4) {
        formErrors = {
          ...formErrors,
          name: { type: 'required', message: f({ id: 'waypoints.edit.title.required.min.char' }) },
        };
      }

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleFormOptions = async () => {
    if (!isCopyModeWp) {
      setShowWaypointLoader(true);
      const response = await getWaypointFormData();

      if (response && waypointData) {
        setFormOptions(response);
        const label = response.customerId.values.find(
          (item) => String(item.value) === String(waypointData.customerId)
        );
        setCustomerLabel(`${label?.item} (${label?.label})`);
        setShowWaypointLoader(false);
      }
    }
    setShowWaypointLoader(false);
  };

  const { isLoading } = useQuery({
    queryKey: ['editWaypointFormOptions', waypointData, isCopyModeWp],
    queryFn: () => handleFormOptions(),
    // staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const updateWaypoints = () => {
    queryClient.invalidateQueries({
      queryKey: [WAYPOINT_QUERY_KEY, selectedCustomers, activeFilter],
    });
  };

  const handleOnSubmit = async (values: EditWaypointFormValues) => {
    const trimName: string = values.name.trim();

    if (waypointData && coordinates && center && waypointId) {
      const data: IAddWaypointRequestData = {
        name: trimName,
        type: values.type ? values.type : waypointData.type,
        centerPoint: center,
        customerId: Number(values.customer),
        area: computedArea,
        categoryId: Number(values.icon),
        coordinates: convertCoordinatesToArray(coordinates, true),
      };

      const response = await updateWaypoint(waypointId, data);

      if (response && response.status === STATUS_WARNING) {
        const message = response?.toast?.message;

        if (message) {
          setError('name', {
            type: 'required',
            message: f({ id: `${message}` }),
          });
        }
        return;
      }

      if (response) {
        setIsEditMode(false);
        props.saveEditWaypoint();
        updateWaypoints();
        setDetailWaypoint(null);
        setWaypointData(null);
      }
    } else if (waypointData && waypointData.coordinates && waypointId) {
      const data: IAddWaypointRequestData = {
        name: trimName,
        type: values.type ? values.type : waypointData.type,
        centerPoint: waypointData.centerPoint,
        customerId: Number(values.customer),
        area: computedArea,
        categoryId: Number(values.icon),
        coordinates: convertCoordinatesToArray(waypointData.coordinates, true),
      };

      const response = await updateWaypoint(waypointId, data);

      if (response && response.status === STATUS_WARNING) {
        const message = response?.toast?.message;

        if (message) {
          setError('name', {
            type: 'required',
            message: f({ id: `${message}` }),
          });
        }
        return;
      }

      if (response) {
        setIsEditMode(false);
        props.saveEditWaypoint();
        updateWaypoints();
        setDetailWaypoint(null);
        setWaypointData(null);
      }
    }
  };

  const handleConfirmDialog = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    setIsOpenConfirmDialog(true);
  };
  const handleContinueConfirmDialog = () => {
    setIsOpenConfirmDialog(false);

    props.closeEditWaypoint();
  };

  const handleCloseConfirmDialog = () => {
    setIsOpenConfirmDialog(false);
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      {isLoading && <CmdLoader inContent={true} />}

      <div className="b-mc-waypoints">
        <div className="b-mc-waypoints-title">
          <a href="#">
            <i className="cmd-icon cmd-icon-back mr-1" onClick={handleConfirmDialog} />
            {f({ id: 'waypoints.edit.main.title' })}
          </a>
        </div>
        <div className="b-mc-waypoints-content">
          {isOpenConfirmDialog && (
            <ConfirmDialog
              title={`${f({
                id: 'waypoint.add.confirm.dialog.title',
              })}`}
              cancelCallback={handleCloseConfirmDialog}
              continueCallback={handleContinueConfirmDialog}
              cancelButtonTitle={f({
                id: 'waypoint.add.confirm.dialog.cancel',
              })}
              continueButtonTitle={f({
                id: 'waypoint.add.confirm.dialog.confirm',
              })}
            >
              {f({ id: 'waypoint.add.confirm.dialog.message' })}
            </ConfirmDialog>
          )}
          {formOptions &&
            waypointData &&
            waypointData.customerId &&
            formOptions.customerId.values &&
            customerLabel && (
              <CmdLabel
                id="customer"
                title={f({ id: 'waypoints.form.customer.title' })}
                value={customerLabel}
              />
            )}

          {formOptions && formOptions.categoryId && (
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithIcon
                  {...field}
                  // name="icon"
                  // id="icon"
                  label={f({ id: 'waypoints.form.category.title' })}
                  options={formOptions.categoryId.values}
                  introOption={f({ id: 'form.choose' })}
                  control={control}
                  required={true}
                />
              )}
            />
          )}

          {formOptions && (
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="name"
                  type="text"
                  // labelClassName="col-3 form-label"
                  label={f({ id: 'waypoints.edit.title' })}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  placeholder={f({ id: 'waypoints.edit.title.required.placeholder' })}
                  onChangeCallback={(value) => {
                    setValue('name', value);
                    trigger('name');
                  }}
                  required={true}
                  error={errors.name?.message}
                  touched={!!errors.name}
                />
              )}
            />
          )}

          {formOptions && waypointData && (
            <CmdLabel
              id="type"
              title={f({ id: 'waypoints.edit.type' })}
              value={waypointData.typeName}
            />
          )}
        </div>
        <div className="b-mc-waypoints-footer">
          <div className="row">
            <div className="col">
              <a href="#" className="e-button" onClick={handleConfirmDialog}>
                {f({ id: 'waypoints.cancel' })}
              </a>
            </div>
            <div className="col">
              <button
                type="submit"
                className={`${Object.keys(errors).length > 0 ? 'e-button e-button--gray  e-button--disabled' : 'e-button e-button--gray'}`}
              >
                {f({ id: 'waypoints.edit.save' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default EditWaypointForm;
